
import { useState } from 'react';

interface FlowNode {
  id: string;
  type: 'question' | 'solution' | 'action';
  text: string;
  yesNext?: string;
  noNext?: string;
  icon?: string;
}

const flowchartData: Record<string, FlowNode[]> = {
  video: [
    { id: 'v1', type: 'question', text: 'カメラは物理的に接続されていますか？', yesNext: 'v2', noNext: 'v1-sol' },
    { id: 'v1-sol', type: 'solution', text: 'カメラをUSBポートまたはHDMIに接続してください。別のポートも試してみてください。', icon: 'ri-usb-line' },
    { id: 'v2', type: 'question', text: 'デバイスマネージャーでカメラが認識されていますか？', yesNext: 'v3', noNext: 'v2-sol' },
    { id: 'v2-sol', type: 'solution', text: 'カメラドライバーを再インストールしてください。PCを再起動後、再度確認してください。', icon: 'ri-install-line' },
    { id: 'v3', type: 'question', text: '配信ソフトでカメラを選択できますか？', yesNext: 'v4', noNext: 'v3-sol' },
    { id: 'v3-sol', type: 'solution', text: '他のアプリがカメラを使用していないか確認してください。配信ソフトを再起動してください。', icon: 'ri-restart-line' },
    { id: 'v4', type: 'question', text: '映像は正常に表示されていますか？', yesNext: 'v4-ok', noNext: 'v4-sol' },
    { id: 'v4-sol', type: 'solution', text: '解像度・フレームレート設定を下げてみてください。カメラの設定をリセットしてください。', icon: 'ri-settings-3-line' },
    { id: 'v4-ok', type: 'action', text: '映像系統は正常です！音声やネットワークの診断も行ってください。', icon: 'ri-check-double-line' },
  ],
  audio: [
    { id: 'a1', type: 'question', text: 'マイクは物理的に接続されていますか？', yesNext: 'a2', noNext: 'a1-sol' },
    { id: 'a1-sol', type: 'solution', text: 'マイクを正しいポート（USB/オーディオジャック）に接続してください。', icon: 'ri-mic-line' },
    { id: 'a2', type: 'question', text: 'システム設定でマイクが認識されていますか？', yesNext: 'a3', noNext: 'a2-sol' },
    { id: 'a2-sol', type: 'solution', text: 'サウンド設定を開き、入力デバイスを確認してください。ドライバーの更新も試してください。', icon: 'ri-sound-module-line' },
    { id: 'a3', type: 'question', text: 'マイクはミュートになっていませんか？', yesNext: 'a4', noNext: 'a3-sol' },
    { id: 'a3-sol', type: 'solution', text: 'マイク本体のミュートスイッチ、システム設定、配信ソフトのミュート状態を確認してください。', icon: 'ri-volume-mute-line' },
    { id: 'a4', type: 'question', text: '音声レベルは適切ですか（-12dB〜-6dB）？', yesNext: 'a4-ok', noNext: 'a4-sol' },
    { id: 'a4-sol', type: 'solution', text: 'マイクゲインを調整してください。コンプレッサーやリミッターの使用も検討してください。', icon: 'ri-equalizer-line' },
    { id: 'a4-ok', type: 'action', text: '音声系統は正常です！配信テストで最終確認を行ってください。', icon: 'ri-check-double-line' },
  ],
  network: [
    { id: 'n1', type: 'question', text: 'インターネットに接続されていますか？', yesNext: 'n2', noNext: 'n1-sol' },
    { id: 'n1-sol', type: 'solution', text: 'ルーターを再起動してください。LANケーブルの接続を確認してください。', icon: 'ri-router-line' },
    { id: 'n2', type: 'question', text: '上り速度は10Mbps以上ありますか？', yesNext: 'n3', noNext: 'n2-sol' },
    { id: 'n2-sol', type: 'solution', text: '有線LAN接続に切り替えてください。他のデバイスの通信を制限してください。', icon: 'ri-speed-line' },
    { id: 'n3', type: 'question', text: 'VPNやプロキシを使用していませんか？', yesNext: 'n4', noNext: 'n3-sol' },
    { id: 'n3-sol', type: 'solution', text: 'VPN/プロキシを無効にして配信してください。ファイアウォール設定も確認してください。', icon: 'ri-shield-cross-line' },
    { id: 'n4', type: 'question', text: '配信プラットフォームに接続できますか？', yesNext: 'n4-ok', noNext: 'n4-sol' },
    { id: 'n4-sol', type: 'solution', text: 'ストリームキーとサーバーURLを再確認してください。別のサーバーを試してください。', icon: 'ri-server-line' },
    { id: 'n4-ok', type: 'action', text: 'ネットワークは正常です！ビットレートを適切に設定して配信を開始してください。', icon: 'ri-check-double-line' },
  ],
};

const troubleCategories = [
  { id: 'video', label: '映像が映らない', icon: 'ri-camera-off-line', color: 'from-rose-500 to-pink-500' },
  { id: 'audio', label: '音声が出ない', icon: 'ri-volume-mute-line', color: 'from-amber-500 to-orange-500' },
  { id: 'network', label: '接続できない', icon: 'ri-wifi-off-line', color: 'from-cyan-500 to-teal-500' },
];

export default function DiagnosticFlowchart() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [currentNodeId, setCurrentNodeId] = useState<string | null>(null);
  const [history, setHistory] = useState<string[]>([]);
  const [isExpanded, setIsExpanded] = useState(true);

  const startDiagnosis = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setCurrentNodeId(flowchartData[categoryId][0].id);
    setHistory([]);
  };

  const resetDiagnosis = () => {
    setSelectedCategory(null);
    setCurrentNodeId(null);
    setHistory([]);
  };

  const handleAnswer = (answer: 'yes' | 'no') => {
    if (!selectedCategory || !currentNodeId) return;
    
    const nodes = flowchartData[selectedCategory];
    const currentNode = nodes.find(n => n.id === currentNodeId);
    
    if (!currentNode) return;
    
    const nextId = answer === 'yes' ? currentNode.yesNext : currentNode.noNext;
    
    if (nextId) {
      setHistory([...history, currentNodeId]);
      setCurrentNodeId(nextId);
    }
  };

  const goBack = () => {
    if (history.length > 0) {
      const newHistory = [...history];
      const previousId = newHistory.pop();
      setHistory(newHistory);
      setCurrentNodeId(previousId || null);
    } else {
      resetDiagnosis();
    }
  };

  const getCurrentNode = () => {
    if (!selectedCategory || !currentNodeId) return null;
    return flowchartData[selectedCategory].find(n => n.id === currentNodeId);
  };

  const currentNode = getCurrentNode();
  const currentCategory = troubleCategories.find(c => c.id === selectedCategory);

  return (
    <div className="my-8 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200 overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-gray-700 to-gray-800 text-white cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg">
            <i className="ri-git-branch-line text-xl"></i>
          </div>
          <div className="text-left">
            <h3 className="font-bold text-lg">トラブル診断フローチャート</h3>
            <p className="text-gray-300 text-sm">質問に答えて原因を特定</p>
          </div>
        </div>
        <div className={`w-6 h-6 flex items-center justify-center transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
          <i className="ri-arrow-down-s-line text-xl"></i>
        </div>
      </button>

      <div className={`overflow-hidden transition-all duration-500 ${isExpanded ? 'max-h-[1000px]' : 'max-h-0'}`}>
        <div className="p-6">
          {!selectedCategory ? (
            /* カテゴリ選択画面 */
            <div>
              <p className="text-gray-600 mb-4 text-center">どのようなトラブルが発生していますか？</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {troubleCategories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => startDiagnosis(category.id)}
                    className={`p-5 rounded-xl bg-gradient-to-br ${category.color} text-white hover:scale-105 transition-transform cursor-pointer shadow-lg`}
                  >
                    <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-3">
                      <i className={`${category.icon} text-2xl`}></i>
                    </div>
                    <span className="font-semibold">{category.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            /* 診断画面 */
            <div>
              {/* ヘッダー */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br ${currentCategory?.color} text-white`}>
                    <i className={currentCategory?.icon || ''}></i>
                  </div>
                  <span className="font-semibold text-gray-900">{currentCategory?.label}の診断</span>
                </div>
                <button
                  onClick={resetDiagnosis}
                  className="text-sm text-gray-500 hover:text-gray-700 cursor-pointer flex items-center gap-1"
                >
                  <i className="ri-refresh-line"></i>
                  最初から
                </button>
              </div>

              {/* 進捗インジケーター */}
              <div className="flex items-center gap-2 mb-6">
                {flowchartData[selectedCategory].filter(n => n.type === 'question').map((_, index) => (
                  <div
                    key={index}
                    className={`h-2 flex-1 rounded-full transition-colors ${
                      index <= history.length ? 'bg-cyan-500' : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>

              {/* 現在のノード */}
              {currentNode && (
                <div className={`p-6 rounded-xl ${
                  currentNode.type === 'question' 
                    ? 'bg-white border-2 border-cyan-200' 
                    : currentNode.type === 'solution'
                    ? 'bg-amber-50 border-2 border-amber-200'
                    : 'bg-teal-50 border-2 border-teal-200'
                }`}>
                  {currentNode.type === 'question' ? (
                    <>
                      <div className="flex items-start gap-3 mb-6">
                        <div className="w-10 h-10 flex items-center justify-center bg-cyan-100 text-cyan-600 rounded-full flex-shrink-0">
                          <i className="ri-question-line text-xl"></i>
                        </div>
                        <p className="text-lg font-medium text-gray-900 pt-1">{currentNode.text}</p>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleAnswer('yes')}
                          className="flex-1 py-3 px-6 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors cursor-pointer font-medium flex items-center justify-center gap-2 whitespace-nowrap"
                        >
                          <i className="ri-check-line"></i>
                          はい
                        </button>
                        <button
                          onClick={() => handleAnswer('no')}
                          className="flex-1 py-3 px-6 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors cursor-pointer font-medium flex items-center justify-center gap-2 whitespace-nowrap"
                        >
                          <i className="ri-close-line"></i>
                          いいえ
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-start gap-3 mb-4">
                        <div className={`w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 ${
                          currentNode.type === 'solution' 
                            ? 'bg-amber-100 text-amber-600' 
                            : 'bg-teal-100 text-teal-600'
                        }`}>
                          <i className={`${currentNode.icon || 'ri-lightbulb-line'} text-xl`}></i>
                        </div>
                        <div>
                          <span className={`text-sm font-medium ${
                            currentNode.type === 'solution' ? 'text-amber-600' : 'text-teal-600'
                          }`}>
                            {currentNode.type === 'solution' ? '解決策' : '診断完了'}
                          </span>
                          <p className="text-gray-900 mt-1">{currentNode.text}</p>
                        </div>
                      </div>
                      <div className="flex gap-3 mt-4">
                        <button
                          onClick={goBack}
                          className="flex-1 py-3 px-6 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors cursor-pointer font-medium flex items-center justify-center gap-2 whitespace-nowrap"
                        >
                          <i className="ri-arrow-left-line"></i>
                          戻る
                        </button>
                        <button
                          onClick={resetDiagnosis}
                          className="flex-1 py-3 px-6 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors cursor-pointer font-medium flex items-center justify-center gap-2 whitespace-nowrap"
                        >
                          <i className="ri-refresh-line"></i>
                          別の診断
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* 戻るボタン（質問中のみ） */}
              {currentNode?.type === 'question' && history.length > 0 && (
                <button
                  onClick={goBack}
                  className="mt-4 text-gray-500 hover:text-gray-700 cursor-pointer flex items-center gap-1 text-sm"
                >
                  <i className="ri-arrow-left-line"></i>
                  前の質問に戻る
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
